/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Set up Probabilistic IDR (Google Cloud Storage)", description = "Learn how to set up Google Cloud Storage permissions and a GCS bucket so Hightouch can run probabilistic identity resolution in your workspace.", lastUpdatedDate = new Date(1773964800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Set up Probabilistic IDR (Google Cloud Storage)",
        description: "Learn how to set up Google Cloud Storage permissions and a GCS bucket so Hightouch can run probabilistic identity resolution in your workspace.",
        lastUpdatedDate: "Fri Mar 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Step 1: Create a custom IAM role",
          "depth": 1,
          "id": "step-1-create-a-custom-iam-role"
        }, {
          "value": "Step 2: Grant the role to the Hightouch service account",
          "depth": 1,
          "id": "step-2-grant-the-role-to-the-hightouch-service-account"
        }, {
          "value": "Step 3: Create a GCS bucket",
          "depth": 1,
          "id": "step-3-create-a-gcs-bucket"
        }, {
          "value": "Step 4: Share the bucket name with Hightouch",
          "depth": 1,
          "id": "step-4-share-the-bucket-name-with-hightouch"
        }, {
          "value": "Step 5: Grant bucket access to the BigQuery service account",
          "depth": 1,
          "id": "step-5-grant-bucket-access-to-the-bigquery-service-account"
        }, {
          "value": "FAQs",
          "depth": 0,
          "id": "faqs"
        }, {
          "value": "Can I reuse an existing GCS bucket?",
          "depth": 1,
          "id": "can-i-reuse-an-existing-gcs-bucket"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      hr: "hr",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      pre: "pre",
      span: "span",
      strong: "strong",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Probabilistic matching does some of its work outside your data warehouse, so it needs a place in your cloud to store data during the process. Hightouch uses it as a workspace during each graph run — reading your source records, running the matching process, and writing results back to your data warehouse. All data stays in your cloud account and under your control."
      }), "\n", _jsx(_components.p, {
        children: "Some data persists in the bucket between runs so Hightouch doesn't have to start from scratch each time."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "step-1-create-a-custom-iam-role",
        children: "Step 1: Create a custom IAM role"
      }), "\n", _jsx(_components.p, {
        children: "Create a new role in your GCP project with the following permissions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "storage.buckets.get"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "storage.objects.list"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "storage.objects.create"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "storage.objects.get"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "storage.objects.delete"
          })
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["gcloud iam roles create YOUR_CUSTOM_ROLE_NAME \\\n  --project=YOUR_PROJECT_ID \\\n  --title=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR CUSTOM ROLE NAME\""
          }), " \\\n  --description=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Custom role for Hightouch probabilistic IDR\""
          }), " \\\n  --permissions=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"storage.buckets.get,storage.objects.list,storage.objects.create,storage.objects.get,storage.objects.delete\""
          }), "\n"]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "step-2-grant-the-role-to-the-hightouch-service-account",
        children: "Step 2: Grant the role to the Hightouch service account"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch will provide a service account specifically for probabilistic IDR."
      }), "\n", _jsx(_components.p, {
        children: "Once you have the service account email, grant it the custom role:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "gcloud projects add-iam-policy-binding YOUR_PROJECT_ID \\\n  --member=serviceAccount:HT_PROVIDED_SERVICE_ACCOUNT@YOUR_DOMAIN.iam.gserviceaccount.com \\\n  --role=YOUR_CUSTOM_ROLE_NAME\n"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "step-3-create-a-gcs-bucket",
        children: "Step 3: Create a GCS bucket"
      }), "\n", _jsx(_components.p, {
        children: "Create a GCS bucket with the following requirements:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Located in the same cloud provider and region"
          }), " as your Hightouch workspace"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Must not have object lifecycle rules"
          }), " that delete or expire objects in the following path:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["/workspace-", _jsx(_components.span, {
            className: "hljs-variable",
            children: "$WORKSPACE_ID"
          }), "/datalake\n"]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "step-4-share-the-bucket-name-with-hightouch",
        children: "Step 4: Share the bucket name with Hightouch"
      }), "\n", _jsx(_components.p, {
        children: "Share the bucket name with your Hightouch team so they can complete setup on their side."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "step-5-grant-bucket-access-to-the-bigquery-service-account",
        children: "Step 5: Grant bucket access to the BigQuery service account"
      }), "\n", _jsxs(_components.p, {
        children: ["Grant the ", _jsx(_components.strong, {
          children: "same custom IAM role"
        }), " from Step 1 to the BigQuery service account used by Hightouch in your project."]
      }), "\n", _jsx(_components.p, {
        children: "This ensures Hightouch can read and write IDR data as part of warehouse workflows."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "faqs",
        children: "FAQs"
      }), "\n", _jsx(_components.h3, {
        id: "can-i-reuse-an-existing-gcs-bucket",
        children: "Can I reuse an existing GCS bucket?"
      }), "\n", _jsxs(_components.p, {
        children: ["Yes. You can use the same GCS bucket you use for ", _jsx(_components.a, {
          href: "/security/storage#google-cloud-storage",
          children: "self-hosted external storage"
        }), ", as long as the bucket does not have lifecycle rules that delete objects in the required path."]
      }), "\n", _jsx(_components.p, {
        children: "Note that the service account used for probabilistic IDR is different from the service account generated by the Hightouch app or one you may already use."
      })]
    });
  }
}
export default MDXContent;
